import { isNumber, upperCase } from 'lodash';
import numeral from 'numeral';

import { DEFAULT_PLACEHOLDER } from 'core/constants';

const NumberHelper = {
  DEFAULT_DECIMALS: 2,
  DEFAULT_SMALL_PRICING_DECIMALS: 5,
  DEFAULT_BIG_PRICING_DECIMALS: 2,

  isEmpty: (floatingValue: number): boolean => !(floatingValue > 0),

  isNonEmpty: (floatingValue: number): boolean => floatingValue > 0,

  toFloat: (numAsString: string): number => parseFloat(numAsString) || null,

  // Check whether the first value is equal to the second one considering given
  // precision.
  areEqual: (
    firstValue: number,
    secondValue: number,
    decimals = NumberHelper.DEFAULT_DECIMALS
  ): boolean => {
    const precision = Math.pow(10, -decimals);

    return Math.abs(firstValue - secondValue) < precision;
  },

  // Compare first value with the second one considering given precision.
  compare: (first: string, second: string, precision: number): number | null => {
    const firstValue = NumberHelper.toFloat(first);
    const secondValue = NumberHelper.toFloat(second);

    if (isNaN(firstValue) || isNaN(secondValue)) {
      return null;
    }

    if (!isNumber(firstValue) || !isNumber(secondValue)) {
      return null;
    }

    if (NumberHelper.areEqual(firstValue, secondValue, precision)) {
      return 0;
    }

    return firstValue < secondValue ? -1 : 1;
  },

  round: (value: number, decimals = NumberHelper.DEFAULT_DECIMALS): number =>
    value && parseFloat(value.toFixed(decimals)),

  getDecimalsToDisplay: (
    amount: number,
    decimals: number,
    smallDecimals: number,
    bigDecimals: number
  ) => {
    if (amount < 1) {
      return smallDecimals;
    }

    if (decimals === undefined || decimals === null) {
      return amount >= 1 ? bigDecimals : smallDecimals;
    }

    return decimals;
  },

  getDecimalsFormat: (decimals: number, fixedDecimals = false) => {
    if (fixedDecimals) {
      return Array(decimals + 1).join('0');
    }

    let decimalFormat = `${Array(Math.min(decimals, 2) + 1).join('0')}`;
    if (decimals > 2) {
      decimalFormat += `[${Array(decimals - 1).join('0')}]`;
    }

    return decimalFormat;
  },

  getFormat: (
    thousandSeparator: boolean,
    decimals: number,
    format?: string,
    fixedDecimals = false
  ) => {
    if (format) {
      return format;
    }

    const decimalFormat = NumberHelper.getDecimalsFormat(decimals, fixedDecimals);

    return `${thousandSeparator ? '0,0' : ''}0.${decimalFormat}`;
  },

  format: ({
    amount,
    displayZero = true,
    format = null,
    thousandSeparator = true,
    decimals = NumberHelper.DEFAULT_DECIMALS,
    smallDecimals = NumberHelper.DEFAULT_SMALL_PRICING_DECIMALS,
    bigDecimals = NumberHelper.DEFAULT_BIG_PRICING_DECIMALS,
  }: {
    amount: number;
    displayZero?: boolean;
    format?: string;
    thousandSeparator?: boolean;
    decimals?: number;
    smallDecimals?: number;
    bigDecimals?: number;
  }): string => {
    if (
      amount === null ||
      amount === undefined ||
      (!displayZero && (!amount || NumberHelper.isEmpty(amount)))
    ) {
      return DEFAULT_PLACEHOLDER;
    }

    return numeral(amount).format(
      `${NumberHelper.getFormat(
        thousandSeparator,
        NumberHelper.getDecimalsToDisplay(Number(amount), decimals, smallDecimals, bigDecimals),
        format
      )}`
    );
  },

  formatNumberPerUom: ({
    amount,
    uom,
    uomQuantity = null,
    decimals = NumberHelper.DEFAULT_DECIMALS,
    displayZero = true,
    format = null,
    thousandSeparator = true,
    smallDecimals = NumberHelper.DEFAULT_SMALL_PRICING_DECIMALS,
    bigDecimals = NumberHelper.DEFAULT_BIG_PRICING_DECIMALS,
  }: {
    amount: number;
    uom: string;
    uomQuantity?: number;
    decimals?: number;
    displayZero?: boolean;
    format?: string;
    thousandSeparator?: boolean;
    smallDecimals?: number;
    bigDecimals?: number;
  }) => {
    if (!displayZero && !(amount > 0)) {
      return DEFAULT_PLACEHOLDER;
    }

    const number = NumberHelper.format({
      amount,
      displayZero,
      format,
      thousandSeparator,
      decimals,
      smallDecimals,
      bigDecimals,
    });

    const perUom = NumberHelper.formatPerUom(uom, uomQuantity);

    return `${number} ${perUom}`;
  },

  formatPerUom: (uom: string, uomQuantity: number) => {
    if (!uom) {
      return '';
    }

    const formattedUom = upperCase(uom);

    const uomQuantityFormatted = NumberHelper.formatUomQuantity(uomQuantity);

    if (uomQuantityFormatted) {
      return `/${uomQuantityFormatted} ${formattedUom}`;
    }

    return `/${formattedUom}`;
  },

  formatUomQuantity: (uomQuantity: number) => {
    const uomQuantityValue = uomQuantity > 0 ? uomQuantity : 1;

    if (1 === uomQuantityValue) {
      return '';
    }

    return numeral(uomQuantity).format('0');
  },

  isIncrement: (value: number, increment: number, tolerance = 0.000001): boolean => {
    const closestMultiple = Math.round(value / increment) * increment;

    return Math.abs(value - closestMultiple) < tolerance;
  },

  formatInteger: ({ amount, displayZero = true }: { amount: number; displayZero?: boolean }) => {
    if (amount === null || amount === undefined || (!displayZero && amount === 0)) {
      return DEFAULT_PLACEHOLDER;
    }

    return numeral(amount).format('0,0');
  },

  formatIntegerPerUom: (amount: number, uom: string, displayZero = false) => {
    if (
      amount === null ||
      amount === undefined ||
      (!displayZero && (!amount || NumberHelper.isEmpty(amount)))
    ) {
      return DEFAULT_PLACEHOLDER;
    }

    const formattedAmount = NumberHelper.formatInteger({ amount });

    if (!uom) {
      return formattedAmount;
    }

    return `${formattedAmount} ${uom}`;
  },
};

export default NumberHelper;
